import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Trash2, RotateCcw, Search, ShieldAlert, Loader2, Archive } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

const PAGE = 50;
const REASONS = ['test_residue', 'legacy_archive', 'manual_archive', 'duplicate', 'obsolete', 'founder_request'];
// Phase 2 Governed Cleanup — every registered archivable type.
const ENTITY_TYPES = [
  'questionnaire', 'competency', 'company', 'commercial_lead', 'role_profile',
  'climara_campaign', 'training_action', 'talentheart_survey',
  'evaluation_request', 'recruitment_process', 'assignment', 'talentfix_case',
  'talentexit_case',
];

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

export default function AdminTrash() {
  const { t } = useTranslation();
  const { user } = useApp();
  const [entityType, setEntityType] = useState('questionnaire');
  const [q, setQ] = useState('');
  const [reason, setReason] = useState('all');
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null); // {item, dep}

  const load = useCallback(async (et, query, rc, sk) => {
    setLoading(true);
    try {
      const params = { entity_type: et, limit: PAGE, skip: sk };
      if (query.trim()) params.q = query.trim();
      if (rc !== 'all') params.reason_code = rc;
      const { data: d } = await api.get('/admin/trash', { params });
      setData(d);
    } catch (e) {
      toast.error(t('trash.loadFailed', { defaultValue: 'Could not load the trash' }), { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    if (user && user.role !== 'hm_admin') return undefined;
    const timer = setTimeout(() => load(entityType, q, reason, skip), q ? 350 : 0);
    return () => clearTimeout(timer);
  }, [entityType, q, reason, skip, load, user]);

  const restore = async (item) => {
    setBusyId(item.id);
    try {
      await api.post(`/admin/trash/${entityType}/${item.id}/restore`);
      toast.success(t('trash.restoredToast', { defaultValue: 'Restored successfully' }), { description: item.name });
      load(entityType, q, reason, skip);
    } catch (e) {
      toast.error(t('trash.restoreFailed', { defaultValue: 'Restore failed' }), { description: formatApiError(e) });
    } finally { setBusyId(null); }
  };

  const switchTab = (v) => { setEntityType(v); setSkip(0); };

  const preflightDelete = async (item) => {
    setBusyId(item.id);
    try {
      const { data: dep } = await api.get(`/admin/trash/${entityType}/${item.id}/dependencies`);
      if (dep.safe) setConfirmDel({ item, dep });
      else {
        const parts = (dep.blockers || []).map((b) => `${b.count} ${t(`trash.blocker.${b.kind}`, { defaultValue: b.kind.replace(/_/g, ' ') })}`).join(', ');
        toast.error(t('trash.deleteBlocked', { defaultValue: 'Cannot delete permanently' }), {
          description: t('trash.deleteBlockedDesc', { defaultValue: 'This record has {{deps}}. It stays archived.', deps: parts }),
        });
      }
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };

  const executeDelete = async () => {
    setBusyId(confirmDel.item.id);
    try {
      await api.delete(`/admin/trash/${entityType}/${confirmDel.item.id}`);
      toast.success(t('trash.deletedToast', { defaultValue: 'Permanently deleted (backup + audit).' }), { description: confirmDel.item.name });
      setConfirmDel(null);
      load(entityType, q, reason, skip);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };

  if (user && user.role !== 'hm_admin') return <Navigate to="/admin" replace />;

  return (
    <>
      <Topbar
        title={t('trash.title', { defaultValue: 'Trash' })}
        subtitle={t('trash.subtitle', { defaultValue: 'Archived records — reversible, audited, admin-only' })}
      />
      <div className="p-6 lg:p-8 space-y-5" data-testid="admin-trash-page">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={entityType} onValueChange={switchTab}>
            <SelectTrigger className="w-72 h-10" data-testid="trash-entity-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENTITY_TYPES.map((et) => (
                <SelectItem key={et} value={et} data-testid={`trash-type-${et}`}>
                  {t(`trash.type.${et}`, { defaultValue: et.replace(/_/g, ' ') })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Card className="p-4 shadow-card flex flex-wrap items-center gap-3" data-testid="trash-toolbar">
          <div className="relative flex-1 min-w-60">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('trash.searchPh', { defaultValue: 'Search by name or code…' })}
              value={q}
              onChange={(e) => { setQ(e.target.value); setSkip(0); }}
              className="pl-9 h-10"
              data-testid="trash-search"
            />
          </div>
          <Select value={reason} onValueChange={(v) => { setReason(v); setSkip(0); }}>
            <SelectTrigger className="w-56" data-testid="trash-reason-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('trash.allReasons', { defaultValue: 'All reasons' })}</SelectItem>
              {REASONS.map((r) => (
                <SelectItem key={r} value={r}>{t(`trash.reason.${r}`, { defaultValue: r.replace(/_/g, ' ') })}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="outline" className="tabular-nums" data-testid="trash-total">
            {t('trash.totalCount', { defaultValue: '{{n}} archived', n: data.total })}
          </Badge>
        </Card>

        <Card className="shadow-card overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-muted-foreground" data-testid="trash-loading">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}
            </div>
          ) : data.items.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground" data-testid="trash-empty">
              <Trash2 className="h-8 w-8 opacity-30 mx-auto mb-2" />
              {t('trash.empty', { defaultValue: 'Nothing in the trash for this filter.' })}
            </div>
          ) : (
            <Table data-testid="trash-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('trash.th.name', { defaultValue: 'Name' })}</TableHead>
                  <TableHead>{t('trash.th.prevStatus', { defaultValue: 'Previous status' })}</TableHead>
                  <TableHead>{t('trash.th.reason', { defaultValue: 'Reason' })}</TableHead>
                  <TableHead>{t('trash.th.archivedAt', { defaultValue: 'Archived' })}</TableHead>
                  <TableHead>{t('trash.th.archivedBy', { defaultValue: 'By' })}</TableHead>
                  <TableHead className="text-right">{t('common.actions', { defaultValue: 'Actions' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((it) => {
                  const st = it.archive_state || {};
                  return (
                    <TableRow key={it.id} data-testid={`trash-row-${it.id}`}>
                      <TableCell>
                        <p className="font-medium">{it.name || it.title || it.position_title || it.company_name || it.suite_code || '—'}</p>
                        {it.code && <p className="text-xs text-muted-foreground font-mono">{it.code}</p>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{st.previous_status || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {t(`trash.reason.${st.reason_code}`, { defaultValue: (st.reason_code || '—').replace(/_/g, ' ') })}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmt(st.archived_at)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{st.archived_by_name || '—'}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" disabled={busyId === it.id} onClick={() => restore(it)}
                                className="gap-1.5" data-testid={`trash-restore-${it.id}`}>
                          <RotateCcw className="h-3.5 w-3.5" /> {t('trash.restore', { defaultValue: 'Restore' })}
                        </Button>
                        <Button size="sm" variant="ghost" disabled={busyId === it.id} onClick={() => preflightDelete(it)}
                                className="ml-1 gap-1.5 text-muted-foreground hover:text-destructive" data-testid={`trash-delete-${it.id}`}>
                          <Trash2 className="h-3.5 w-3.5" /> {t('trash.deletePermanently', { defaultValue: 'Delete' })}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>

        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="trash-permanent-delete-note">
            <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
            {t('trash.permanentGoverned', { defaultValue: 'Permanent deletion is dependency-gated: only records that were never used and have no dependencies can be deleted — backup-first, fully audited. Everything else stays archived and restorable.' })}
          </p>
          {data.total > PAGE && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))} data-testid="trash-prev">
                {t('common.previous', { defaultValue: 'Previous' })}
              </Button>
              <span className="text-xs text-muted-foreground tabular-nums">{skip + 1}–{Math.min(skip + PAGE, data.total)} / {data.total}</span>
              <Button size="sm" variant="outline" disabled={skip + PAGE >= data.total} onClick={() => setSkip(skip + PAGE)} data-testid="trash-next">
                {t('common.next', { defaultValue: 'Next' })}
              </Button>
            </div>
          )}
        </div>

        <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
          <DialogContent className="max-w-md" data-testid="trash-delete-confirm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-destructive" />
                {t('trash.confirmDeleteTitle', { defaultValue: 'Permanent deletion' })}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              {t('trash.confirmDeleteBody', {
                defaultValue: '"{{name}}" has never been used and has no dependencies. A backup will be stored and the deletion will be audited. This action is permanent.',
                name: confirmDel?.item?.name || confirmDel?.item?.id,
              })}
            </p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmDel(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
              <Button variant="destructive" disabled={!!busyId} onClick={executeDelete} data-testid="trash-delete-confirm-btn">
                {t('trash.confirmDeleteBtn', { defaultValue: 'Delete permanently' })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
